// app_core/src/map/area-markers.js
// Manages clickable area pins on the map for unselected areas (UNIFIED mode only)

import { LOGGING, MAP_DATA_STRATEGY } from '/settings.js';
import { normalizeSlug } from '../utils/url-utils.js';
import { createAreaIconElement } from '../utils/icon.js';

let areaMarkerGroup = null; // Layer group for area markers
const areaMarkersMap = new Map(); // Cache markers by area slug for quick access

/**
 * Creates area marker pins for all unselected areas on the map.
 * Only works in UNIFIED mode with SELECT_AREA_FROM_MAP enabled.
 *
 * @param {Array} areas - Array of area objects from API
 * @param {string} selectedAreaSlug - Slug of currently selected area
 * @param {L.Map} mapInstance - Leaflet map instance
 * @returns {L.FeatureGroup} Layer group containing all area markers (for easy management)
 */
export function createAreaMarkers(areas, selectedAreaSlug, mapInstance) {
    // Only create markers if enabled and in UNIFIED mode
    if (!MAP_DATA_STRATEGY.SELECT_AREA_FROM_MAP || MAP_DATA_STRATEGY.TYPE !== 'UNIFIED') {
        if (LOGGING.ENABLED && LOGGING.GENERAL) {
            console.log('[Area Markers] Feature disabled or not in UNIFIED mode');
        }
        return null;
    }

    if (LOGGING.ENABLED && LOGGING.GENERAL) {
        console.log('[Area Markers] Creating area markers for selected area:', selectedAreaSlug);
        console.log('[Area Markers] Total areas:', areas.length);
        console.log('[Area Markers] Areas data:', areas);
    }

    // Clear previous markers
    removeAreaMarkers();

    // Create feature group for area markers (separate from spot cluster group)
    areaMarkerGroup = L.featureGroup();

    let markersCreated = 0;
    // Create marker for each unselected area
    areas.forEach(area => {
        // Only show pins for unselected and active areas
        const isSelected = selectedAreaSlug && normalizeSlug(selectedAreaSlug) === normalizeSlug(area.slug);
        const isActive = area.is_active;

        if (LOGGING.ENABLED && LOGGING.GENERAL) {
            console.log(`[Area Markers] Area: ${area.slug}, isSelected: ${isSelected}, isActive: ${isActive}, lat: ${area.center_lat}, lng: ${area.center_lng}`);
        }

        if (!isSelected && isActive && area.center_lat && area.center_lng) {
            const marker = createAreaMarker(area);
            if (marker) {
                areaMarkersMap.set(area.slug, marker);
                areaMarkerGroup.addLayer(marker);
                markersCreated++;
            }
        }
    });

    // Add to map
    areaMarkerGroup.addTo(mapInstance);

    if (LOGGING.ENABLED && LOGGING.GENERAL) {
        console.log(`[Area Markers] Created ${markersCreated} area markers`);
    }

    return areaMarkerGroup;
}

/**
 * Creates a single area marker (circle with icon).
 * @private
 * @param {Object} area - Area object
 * @returns {L.Marker} Leaflet marker
 */
function createAreaMarker(area) {
    const { center_lat, center_lng, slug, name_en } = area;

    // Create custom div icon (circle with area icon)
    const iconHtml = createAreaMarkerIcon(area);

    const marker = L.marker([center_lat, center_lng], {
        icon: L.divIcon({
            html: iconHtml,
            className: 'area-marker-container',
            iconSize: [100, 110],
            iconAnchor: [50, 40],
            popupAnchor: [0, -40]
        })
    });

    // Store area data on marker for easy access
    marker.areaData = area;

    return marker;
}

/**
 * Creates the HTML for an area marker icon (location pin in primary color with white border and label).
 * @private
 * @param {Object} area - Area object with name_en property
 * @returns {string} HTML string for the icon container with label
 */
function createAreaMarkerIcon(area) {
    const name_en = area.name_en || area.slug || 'Area';

    // Use location-pin-solid icon from the icons sprite
    return `
        <div class="area-marker-pin">
            <svg class="area-marker-icon" width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <use href="/images/icons/icons.svg#location-pin-solid" />
            </svg>
            <div class="area-marker-label">${name_en}</div>
        </div>
    `;
}

/**
 * Removes all area markers from the map.
 */
export function removeAreaMarkers() {
    if (areaMarkerGroup) {
        areaMarkerGroup.remove();
        areaMarkerGroup = null;
    }
    areaMarkersMap.clear();

    if (LOGGING.ENABLED && LOGGING.DEBUG) {
        console.log('[Area Markers] Removed all area markers');
    }
}

/**
 * Updates area markers when area selection changes.
 * Removes marker for newly selected area, recreates all others.
 *
 * @param {Array} areas - Array of area objects
 * @param {string} newSelectedAreaSlug - Slug of newly selected area
 * @param {L.Map} mapInstance - Leaflet map instance
 */
export function updateAreaMarkers(areas, newSelectedAreaSlug, mapInstance) {
    createAreaMarkers(areas, newSelectedAreaSlug, mapInstance);
}

/**
 * Gets the area marker for a specific area slug.
 * @param {string} areaSlug - Area slug
 * @returns {L.Marker|null} Marker if found, null otherwise
 */
export function getAreaMarker(areaSlug) {
    return areaMarkersMap.get(areaSlug) || null;
}

/**
 * Gets all current area markers.
 * @returns {Map} Map of slug -> marker
 */
export function getAllAreaMarkers() {
    return new Map(areaMarkersMap);
}

/**
 * Checks if area markers are currently visible on the map.
 * @returns {boolean}
 */
export function isAreaMarkersVisible() {
    return areaMarkerGroup !== null;
}
